import { useOrganization } from '@/hooks/useOrganization';

/**
 * Provides IVA-inclusive pricing for individual products.
 *
 * IVA (Costa Rica sales tax) only applies when the organization is registered
 * under "regimen tradicional" — represented by settings.iva.enabled being true.
 * The rate lives on the organization row (ivaRate, default 13%).
 *
 * Use this to display the consumer-facing price (price + IVA) anywhere a product
 * price is shown. Tab/sale totals are still computed via useTabCalculations.
 *
 * `showIvaPrice` additionally respects the organization's "Show IVA labels" setting
 * (settings.showIvaLabels, defaults to true) and should be used to gate the display
 * of the IVA-inclusive price.
 */
export const useIvaPricing = () => {
  const { currentOrganization } = useOrganization();

  const ivaEnabled = currentOrganization?.settings?.iva?.enabled ?? false;
  const ivaRate = currentOrganization?.ivaRate ?? 0;
  const showIvaLabels = currentOrganization?.settings?.showIvaLabels ?? true;
  const isIvaApplicable = ivaEnabled && ivaRate > 0;
  const showIvaPrice = isIvaApplicable && showIvaLabels;

  const getPriceWithIva = (price: number): number => {
    if (!isIvaApplicable) return price;
    return price + price * (ivaRate / 100);
  };

  return {
    ivaEnabled,
    ivaRate,
    isIvaApplicable,
    showIvaPrice,
    getPriceWithIva,
  };
};
